/**
 * Validates ProductListHero component props
 * @param {object} props - The props object to validate
 * @param {boolean} [throwOnError=false] - If true, throws an error on validation failure. If false, returns validation result
 * @return {{isValid: boolean, errors: string[]}} ValidationResult object containing isValid flag and array of error messages
 * @throws {Error} if throwOnError is true and validation fails
 */
export const validateProductListHeroProps = (props, throwOnError = false) => {
  const errors = []

  // Check if props is an object
  if (!props || typeof props !== "object") {
    errors.push("Props must be an object")
    if (throwOnError) {
      throw new Error(
        `ProductListHero validation failed:\n${errors.join("\n")}`
      )
    }
    return { isValid: false, errors }
  }

  // Validate required heading
  if (!props.heading) {
    errors.push("heading is required")
  } else {
    if (typeof props.heading !== "object") {
      errors.push("heading must be an object")
    } else {
      if (!props.heading.text) {
        errors.push("heading.text is required")
      }
      if (
        typeof props.heading.text !== "string" &&
        typeof props.heading.text !== "object"
      ) {
        errors.push("heading.text must be a string or React element")
      }
    }
  }

  // Validate optional image
  if (props.image !== undefined) {
    if (typeof props.image !== "object" || props.image === null) {
      errors.push("image must be an object if provided")
    } else {
      if (!props.image.src) {
        errors.push("image.src is required when image is provided")
      }
    }
  }

  // Validate optional video
  if (props.video !== undefined) {
    if (typeof props.video !== "object" || props.video === null) {
      errors.push("video must be an object if provided")
    } else {
      if (!props.video.src) {
        errors.push("video.src is required when video is provided")
      }
    }
  }

  // Validate optional copy
  if (props.copy !== undefined) {
    if (typeof props.copy !== "object" || props.copy === null) {
      errors.push("copy must be an object if provided")
    } else {
      if (!props.copy.text) {
        errors.push("copy.text is required when copy is provided")
      }
      if (
        typeof props.copy.text !== "string" &&
        typeof props.copy.text !== "object"
      ) {
        errors.push("copy.text must be a string or React element")
      }
    }
  }

  // Validate optional CTAs
  if (props.CTAs !== undefined) {
    if (!Array.isArray(props.CTAs)) {
      errors.push("CTAs must be an array if provided")
    } else {
      props.CTAs.forEach((cta, index) => {
        if (typeof cta !== "object" || cta === null) {
          errors.push(`CTAs[${index}] must be an object`)
        } else {
          if (!cta.text) {
            errors.push(`CTAs[${index}].text is required`)
          }
          if (!cta.href && !cta.onClick) {
            errors.push(`CTAs[${index}] must have either href or onClick`)
          }
        }
      })
    }
  }

  // Validate optional products
  if (props.products !== undefined) {
    if (!Array.isArray(props.products)) {
      errors.push("products must be an array if provided")
    } else {
      props.products.forEach((product, index) => {
        if (typeof product !== "object" || product === null) {
          errors.push(`products[${index}] must be an object`)
        } else {
          // Validate required image
          if (!product.image) {
            errors.push(`products[${index}].image is required`)
          } else {
            if (typeof product.image !== "object") {
              errors.push(`products[${index}].image must be an object`)
            } else {
              if (!product.image.src) {
                errors.push(`products[${index}].image.src is required`)
              }
            }
          }

          // Validate required title
          if (!product.title) {
            errors.push(`products[${index}].title is required`)
          } else {
            if (typeof product.title !== "object") {
              errors.push(`products[${index}].title must be an object`)
            } else {
              if (!product.title.text) {
                errors.push(`products[${index}].title.text is required`)
              }
            }
          }

          // Validate href or onClick
          if (!product.href && !product.onClick) {
            errors.push(`products[${index}] must have either href or onClick`)
          }
        }
      })
    }
  }

  const isValid = errors.length === 0

  if (!isValid && throwOnError) {
    throw new Error(`ProductListHero validation failed:\n${errors.join("\n")}`)
  }

  return { isValid, errors }
}
